import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, Clock, TrendingUp, Search, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../services/api';

const BUCKET_CONFIG = {
  current:  { label: 'Current',     color: 'bg-emerald-50 border-emerald-200 text-emerald-700', dot: 'bg-emerald-400', badge: 'bg-emerald-100 text-emerald-700' },
  '1_30':   { label: '1–30 days',   color: 'bg-amber-50 border-amber-200 text-amber-700',       dot: 'bg-amber-400',   badge: 'bg-amber-100 text-amber-700'   },
  '31_60':  { label: '31–60 days',  color: 'bg-orange-50 border-orange-200 text-orange-700',    dot: 'bg-orange-500',  badge: 'bg-orange-100 text-orange-700'  },
  '61_90':  { label: '61–90 days',  color: 'bg-red-50 border-red-200 text-red-700',             dot: 'bg-red-500',     badge: 'bg-red-100 text-red-700'        },
  '90_plus':{ label: '90+ days',    color: 'bg-rose-50 border-rose-200 text-rose-800',          dot: 'bg-rose-600',    badge: 'bg-rose-100 text-rose-800'      },
};

function fmt(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);
}

export default function AgedReceivables() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [bucket, setBucket]       = useState('all');
  const [expanded, setExpanded]   = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get('/api/accounts/reports/aged-receivables')
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  if (error) return (
    <div className="p-6 text-red-600 text-sm flex items-center gap-2">
      <AlertCircle size={16} /> {error}
    </div>
  );

  const { bucket_summary = [], invoices = [], grand_total = 0, as_of } = data || {};

  const filtered = invoices.filter(inv => {
    const matchSearch = !search || inv.contact_name.toLowerCase().includes(search.toLowerCase())
      || (inv.invoice_number || '').toLowerCase().includes(search.toLowerCase());
    const matchBucket = bucket === 'all' || inv.bucket === bucket;
    return matchSearch && matchBucket;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Aged Receivables</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Outstanding invoices by age · As of {as_of ? new Date(as_of).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Total Outstanding:</span>
          <span className="text-lg font-bold text-slate-900">{fmt(grand_total)}</span>
          <button
            onClick={load}
            data-testid="aged-refresh"
            className="ml-2 p-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Bucket Summary Cards */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {bucket_summary.map(b => {
          const cfg = BUCKET_CONFIG[b.key] || {};
          const pct = grand_total > 0 ? Math.round((b.total / grand_total) * 100) : 0;
          return (
            <button
              key={b.key}
              data-testid={`bucket-card-${b.key}`}
              onClick={() => setBucket(bucket === b.key ? 'all' : b.key)}
              className={`border rounded-xl p-4 text-left transition-all hover:shadow-md ${cfg.color} ${bucket === b.key ? 'ring-2 ring-offset-1 ring-current shadow-md' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">{b.label}</span>
                <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
              </div>
              <div className="text-xl font-bold">{fmt(b.total)}</div>
              <div className="text-xs opacity-70 mt-1">{b.count} invoice{b.count !== 1 ? 's' : ''} · {pct}%</div>
            </button>
          );
        })}
      </div>

      {/* Aged Bar Visualisation */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="text-xs font-medium text-slate-600 mb-3">Balance Distribution</div>
        <div className="flex h-5 rounded-full overflow-hidden gap-0.5">
          {bucket_summary.map(b => {
            const cfg = BUCKET_CONFIG[b.key] || {};
            const pct = grand_total > 0 ? (b.total / grand_total) * 100 : 0;
            if (pct < 0.5) return null;
            return (
              <div
                key={b.key}
                style={{ width: `${pct}%` }}
                className={`${cfg.dot} transition-all duration-500 first:rounded-l-full last:rounded-r-full`}
                title={`${b.label}: ${fmt(b.total)} (${Math.round(pct)}%)`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-2">
          {bucket_summary.filter(b => grand_total > 0 && (b.total / grand_total) * 100 >= 0.5).map(b => {
            const cfg = BUCKET_CONFIG[b.key] || {};
            const pct = grand_total > 0 ? Math.round((b.total / grand_total) * 100) : 0;
            return (
              <div key={b.key} className="flex items-center gap-1 text-[10px] text-slate-500">
                <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                {b.label} {pct}%
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="aged-search"
            className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-400"
            placeholder="Search contact or invoice…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setBucket('all')}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${bucket === 'all' ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            All buckets
          </button>
          {bucket_summary.filter(b => b.count > 0).map(b => {
            const cfg = BUCKET_CONFIG[b.key] || {};
            return (
              <button
                key={b.key}
                onClick={() => setBucket(bucket === b.key ? 'all' : b.key)}
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${bucket === b.key ? 'bg-slate-800 text-white' : `${cfg.badge} hover:opacity-80`}`}
              >
                {b.label}
              </button>
            );
          })}
        </div>
        <span className="ml-auto text-xs text-slate-500">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Invoice Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left">
              <th className="px-4 py-2.5 font-semibold text-slate-600 w-6"></th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Invoice</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Contact</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Invoice Date</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Due Date</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600 text-right">Invoice Total</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600 text-right">Amount Paid</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600 text-right">Outstanding</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Age</th>
              <th className="px-4 py-2.5 font-semibold text-slate-600">Bucket</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-4 py-12 text-center text-slate-400">
                  No outstanding invoices found
                </td>
              </tr>
            ) : filtered.map(inv => {
              const cfg = BUCKET_CONFIG[inv.bucket] || {};
              const isOverdue = inv.bucket !== 'current';
              return (
                <tr
                  key={inv.invoice_id}
                  data-testid={`ar-row-${inv.invoice_id}`}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot || 'bg-slate-300'}`} />
                  </td>
                  <td className="px-4 py-3 font-medium text-indigo-700">{inv.invoice_number || inv.invoice_id.slice(-8)}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">{inv.contact_name}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                  </td>
                  <td className={`px-4 py-3 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">{fmt(inv.total)}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{fmt(inv.amount_paid)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(inv.amount_due)}</td>
                  <td className="px-4 py-3">
                    {inv.bucket === 'current' ? (
                      <span className="text-emerald-600 text-[10px]">Not due</span>
                    ) : (
                      <span className={`font-medium ${inv.days_overdue > 90 ? 'text-rose-700' : inv.days_overdue > 60 ? 'text-red-600' : inv.days_overdue > 30 ? 'text-orange-600' : 'text-amber-600'}`}>
                        {inv.days_overdue}d
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td colSpan="7" className="px-4 py-3 text-xs font-semibold text-slate-600">
                  Total ({filtered.length} invoices)
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">
                  {fmt(filtered.reduce((s, i) => s + i.amount_due, 0))}
                </td>
                <td colSpan="2" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
